import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineFlightCreditIcon from 'LuxKit/Icons/line/LineFlightCreditIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCruiseBundleHotelItemNames, getMinBundleFlightCredit } from 'lib/cruises/cruiseBundleFlightCredits'

const BundleSectionContent = styled(VerticalSpacer)`
  padding: 0 ${rem(4)};
`

const InclusionSection = styled.div`
  padding: 0 ${rem(4)};
`

interface Props {
  bundle: App.CruiseOfferBundle
}

function CruiseBundleInclusions({ bundle }: Props) {
  const intl = useIntl()
  const hotelBundleItemNames = getCruiseBundleHotelItemNames(bundle)
  const bundleMinFlightCredit = getMinBundleFlightCredit(bundle)
  const hasBundleFlightCredits = bundleMinFlightCredit > 0

  return <Group data-testid="cruise-offer-bundle-inclusions" direction="vertical" gap={20}>
    {hotelBundleItemNames.length > 0 && <>
      <Divider kind="primary" />
      <Group direction="vertical" gap={8}>
        <BodyTextBlock
          variant="large"
          weight="bold"
          startIcon={<LineBedIcon colour="highlight-primary-dark" size="S" />}
        >
          {intl.formatMessage({
            id: 'CSBJ-jSuHiSUQsjPNnvn',
            defaultMessage: 'Hotel stays',
            description:
              'Section heading on cruise offer page for bundle inclusions that contain pre-cruise or post-cruise accommodation items.',
          })}
        </BodyTextBlock>

        <InclusionSection>
          <BundleSectionContent gap={4}>
            {hotelBundleItemNames.map((hotelBundleItemName, index) => <BodyTextBlock
              key={`${hotelBundleItemName}-${index}`}
              variant="medium"
            >
              {hotelBundleItemName}
            </BodyTextBlock>)}
          </BundleSectionContent>
        </InclusionSection>
      </Group>
    </>}

    {hasBundleFlightCredits && <>
      <Divider kind="primary" />
      <Group direction="vertical" gap={8}>
        <BodyTextBlock
          variant="large"
          weight="bold"
          startIcon={<LineFlightCreditIcon colour="highlight-primary-dark" size="S" />}
        >
          {intl.formatMessage({
            id: 'DS90b9K6YjQWvevSSVG0',
            defaultMessage: 'Flight credits',
            description:
              'Section heading on cruise offer page for bundle inclusions that provide a flight credit amount for selected packages.',
          })}
        </BodyTextBlock>

        <InclusionSection>
          <BundleSectionContent gap={4}>
            <BodyTextBlock variant="medium">
              <FormattedMessage
                id="OQVewr2VDLwXciNq-Ii6"
                defaultMessage="from {amount} per person"
                description="Lowest flight credit value shown in cruise offer-page bundle inclusions, followed by a per-person qualifier."
                values={{ amount: <FormatCurrency value={bundleMinFlightCredit} /> }}
              />
            </BodyTextBlock>
            <BodyTextBlock variant="small">
              <FormattedMessage
                id="Yf9fZl2c1svVHOb5OK3w"
                defaultMessage="See fine print on how to redeem"
                description="Helper text in cruise offer bundle flight credits section directing customers to fine print redemption details."
              />
            </BodyTextBlock>
          </BundleSectionContent>
        </InclusionSection>
      </Group>
    </>}
  </Group>
}

export default CruiseBundleInclusions
